import './Page.scss';
import * as React from 'react';
import { LayerHost } from '@fluentui/react';
import { useOktaAuth } from '@okta/okta-react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import FeatureFlagDialog from '../../components/dialog/FeatureFlagDialog';
import { GoToVisitDialog } from '../../components/dialog/GoToVisitDialog';
import { ROUTES } from '../../config/routes';
import { loginSuccess } from '../../redux/auth/actions';
import { RootState } from '../../redux/types';
import { css } from '../../util/Css';
import { ShortcutManager } from '../../util/ShortcutManager';
import { CheckCoverageDialog } from '../dialog/CheckCoverageDialog';
import { ShortcutsDialog } from '../dialog/ShortcutsDialog';
import { ErrorBoundary } from './ErrorBoundary';
import { Navigation } from './Navigation';
import ReduxToastr from 'react-redux-toastr';
import { isAuthorized, resolveUsername } from '../../okta';

declare const healConfig: any;
export interface BuildInfo {
  branch: string;
  buildNumber: string;
  commit: string;
  version: string;
}

export function Page() {
  const [authorized, setAuthorized] = useState(false);
  const [showFeatureFlagDialog, setShowFeatureFlagDialog] = useState(false);
  const [buildInfo, setBuildInfo] = useState<BuildInfo>();

  const ui = useSelector((state: RootState) => state.ui);

  const { oktaAuth, authState } = useOktaAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const registerShortcuts = useCallback(() => {
    ShortcutManager.getInstance().registerShortcut('?', () => ShortcutsDialog.show(), 'Global', 'Show keyboard shortcuts');
    ShortcutManager.getInstance().registerShortcut('g d', () => navigate(ROUTES.dashboard), 'Global', 'Go to dashboard');
    ShortcutManager.getInstance().registerShortcut('g s', () => navigate(ROUTES.search), 'Global', 'Go to search');
    ShortcutManager.getInstance().registerShortcut('g m', () => navigate(ROUTES.visitsMap), 'Global', 'Go to visits map');
    ShortcutManager.getInstance().registerShortcut('g v', () => navigate(ROUTES.visits), 'Global', 'Go to visits');
    ShortcutManager.getInstance().registerShortcut('g n', () => navigate(ROUTES.createVisit), 'Global', 'Go to new visit page');
    ShortcutManager.getInstance().registerShortcut('g p', () => navigate(ROUTES.patients), 'Global', 'Go to patients page');
    ShortcutManager.getInstance().registerShortcut('g r', () => navigate(ROUTES.providers), 'Global', 'Go to providers page');
    ShortcutManager.getInstance().registerShortcut('g t', () => navigate(ROUTES.markets), 'Global', 'Go to markets');
    ShortcutManager.getInstance().registerShortcut('g c', () => navigate(ROUTES.claims), 'Global', 'Go to claims');
    ShortcutManager.getInstance().registerShortcut('t i', () => navigate(ROUTES.toolInsurance), 'Tools', 'Check insurance eligibility');
    ShortcutManager.getInstance().registerShortcut('t r', () => navigate(ROUTES.insuranceRules), 'Tools', 'Manage insurance rules');
    ShortcutManager.getInstance().registerShortcut('t s', () => navigate(ROUTES.scheduling), 'Tools', 'Manage DR/MA shift pairings');
    ShortcutManager.getInstance().registerShortcut('t o', () => navigate(ROUTES.toolOptimize), 'Tools', 'Schedule Optimizer');
    ShortcutManager.getInstance().registerShortcut('t a', () => navigate(ROUTES.toolAdmin), 'Tools', 'Admin Tools');
    ShortcutManager.getInstance().registerShortcut('v', () => GoToVisitDialog.show(), 'Tools', 'Go to visit');
    ShortcutManager.getInstance().registerShortcut('z', () => CheckCoverageDialog.show(), 'Tools', 'Check zipcode or address coverage');
    ShortcutManager.getInstance().registerShortcut('option+f', () => setShowFeatureFlagDialog(true), 'Tools', 'Feature flags (top secret)');
    ShortcutManager.getInstance().registerShortcut('h p', () => navigate(ROUTES.plans), 'Work in progress', 'Insurance Plans');
    ShortcutManager.getInstance().registerShortcut('h t', () => navigate(ROUTES.visitsTimeline), 'Work in progress', 'Visits timeline');
    ShortcutManager.getInstance().registerShortcut('h d', () => navigate(ROUTES.visitsProvider), 'Work in progress', 'Provider visits');
  }, [navigate]);

  useEffect(() => {
    if (!authState) {
      return;
    }
    if (authState.isAuthenticated && authState.accessToken) {
      if (isAuthorized(authState.accessToken)) {
        dispatch(loginSuccess(resolveUsername(authState.accessToken)));
        registerShortcuts();
        setAuthorized(true);
      } else {
        navigate(ROUTES.unauthorized, { replace: true });
      }
    }
    if (!authState.isAuthenticated) {
      oktaAuth.signInWithRedirect({ originalUri: window.location.href });
    }
  }, [authState, navigate, registerShortcuts, dispatch, oktaAuth]);

  useEffect(() => {
    const isLocal = process.env.NODE_ENV === 'development';
    if (isLocal) return;

    const env = healConfig.env;
    const baseUrl = env === 'prd' ? 'https://operations.heal.com' : `https://operations.${env}.heal.com`;
    fetch(`${baseUrl}/_healinternal/version.json`)
      .then((response) => response.json() as Promise<{ versionData: BuildInfo }>)
      .then((data) => setBuildInfo(data.versionData))
      .catch(() => {
        /* Intentionally unhandled */
      });
  }, []);

  if (!authorized) {
    return null;
  }

  const rootCss = css('page-root', {
    'sidepanel-open': !!ui.showSidePanel,
    'header-open': ui.expandNav,
  });

  return (
    <div className={rootCss}>
      <header className="page-header flex">
        <Navigation buildInfo={buildInfo} />
      </header>
      <div className="page-content">
        <ErrorBoundary buildInfo={buildInfo}>
          <Outlet />
        </ErrorBoundary>
      </div>
      <ErrorBoundary buildInfo={buildInfo}>
        <LayerHost className="page-sidepanel" id="sidepanel" />
      </ErrorBoundary>
      <FeatureFlagDialog onClose={() => setShowFeatureFlagDialog(false)} hidden={!showFeatureFlagDialog} />
      <ReduxToastr position="bottom-center" transitionIn="fadeIn" transitionOut="fadeOut" progressBar preventDuplicates />
    </div>
  );
}
